import { Alert, Box } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  CertificateRequest,
  ResponseOfCertificate,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import CertificateAccordions from "./CertificateAccordions";
import useTitle from "hooks/useTitle";

const CertificateUpdateForm = () => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { id: certificateId } = useParams<{ id: string }>();
  const { certificateService, certificateDetailsUrl } = useGlobalContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const certificateResult = useQuery<ResponseOfCertificate>(
    ["certificate", certificateId],
    () => certificateService.getCertificate(certificateId!),
    {
      enabled: !!certificateId,
    }
  );

  const certificate = certificateResult.data?.data;
  useTitle(certificate?.name || t({ id: "certificateFormUpdateButton" }));

  const onSubmit = async (values: CertificateRequest) => {
    try {
      var result = await registerPromise(
        certificateService.saveCertificate(values)
      );

      queryClient.invalidateQueries("certificates");
      queryClient.invalidateQueries(["certificate", certificateId]);

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "certificateUpdated" }))
      );

      return result;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  if (certificateResult.isLoading) return <Loader />;
  if (certificateResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="certificateLoadError" />
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={certificateDetailsUrl(certificateId)}
        labelKey="backToDetailsButtonLabel"
      />
      <h2>
        <T id="certificateFormEditTitle" />
      </h2>
      <CertificateAccordions
        certificateId={certificateId}
        entity={certificate}
        onSubmit={onSubmit}
        onCancel={() => {
          navigate(certificateDetailsUrl(certificateId));
        }}
        submitLabel={t({ id: "certificateFormUpdateButton" })}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default CertificateUpdateForm;
